/**
 * The Costs page's supporting cards (costs audit 2026-07-11) — the pieces that
 * make /costs the LIVING single home of every saved expense:
 *
 *  - NeedsCostsStrip: active SKUs with historical sales but no costs — the
 *    permanent "needs your attention" surface with a one-click path into the
 *    costs chat scoped to that product.
 *  - ParkedCostsCard: business-level costs the setup chat parked (team
 *    overrides, seasonal surcharges) — visible, foldable into overhead, or
 *    dismissable. Before this they were stored but shown nowhere.
 *  - InventoryFinancingCard: the LOC terms (#19) — visible AND editable after
 *    onboarding; saving re-derives the per-unit interest server-side.
 *  - ProcessingFeesCard: each processor's true rates + the flat gateway fee —
 *    read-only view of what the chat captured (the chat re-blends on edit).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { invalidateCatalogQueries, relockFinancialQueries } from "../lib/query-invalidation.js";
import { Card, CardHead, moneyFmt } from "./cards.js";

/* ------------------------- needs-attention strip ------------------------- */

interface ActionItemSku {
  skuId: string;
  skuCode: string;
  label: string;
  productName: string | null;
  units: number;
  revenue: number;
  lastSoldAt: string | null;
  classified: boolean;
}
interface ActionItems {
  needsCosts: ActionItemSku[];
  needsCostsCount: number;
  needsCategorizationCount: number;
  historicalNeedsCosts: ActionItemSku[];
  historicalNeedsCostsCount: number;
  historicalRevenueWithoutCosts: number;
}

const SHOW_ROWS = 6;

/** Un-costed SKUs with recorded sales anywhere in imported history.
 *  Lives permanently at the top of /costs (the page whose job it is), unlike
 *  the removed dashboard banner. `onCostProduct` drops a ready-made ask into
 *  the costs chat below. */
export function NeedsCostsStrip({ onCostProduct, canEdit }: { onCostProduct: (ask: string) => void; canEdit: boolean }): React.ReactElement | null {
  const api = useApiClient();
  const q = useQuery({
    queryKey: ["action-items"],
    queryFn: () => api.get<ActionItems>("/dashboard/action-items"),
  });
  const [showAll, setShowAll] = useState(false);
  const data = q.data;
  if (!data || (data.needsCostsCount === 0 && data.historicalNeedsCostsCount === 0)) return null;
  const rows = showAll ? data.needsCosts : data.needsCosts.slice(0, SHOW_ROWS);
  const n = data.needsCostsCount;
  return (
    <div id="missing-costs" style={{ scrollMarginTop: 18 }}>
    {n > 0 && <div style={{
      marginBottom: data.historicalNeedsCostsCount > 0 ? 12 : 26, padding: "14px 16px", borderRadius: 12,
      border: "1px solid color-mix(in srgb, var(--c2) 40%, var(--border))",
      background: "color-mix(in srgb, var(--c2) 7%, transparent)",
    }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 2 }}>
        Needs your attention: {n} active SKU/offer {n === 1 ? "entry needs" : "entries need"} costs
      </div>
      <div style={{ fontSize: 12.5, color: "var(--body)", marginBottom: 10, lineHeight: 1.5 }}>
        These entries have recorded sales somewhere in the imported history. They are not a count of products selling recently.
        Their cost inputs need review before profit can be verified.
        {data.needsCategorizationCount > 0 && (
          <> {data.needsCategorizationCount === 1 ? "One isn't" : `${data.needsCategorizationCount} aren't`} sorted
          into a product yet — those start on the Categorize step.</>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r) => (
          <div key={r.skuId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{r.label}</span>
              <span style={{ color: "var(--faint)", fontSize: 11, fontFamily: "var(--font-mono)" }}>{r.skuCode}</span>
            </span>
            <span style={{ fontSize: 12, color: "var(--body)", whiteSpace: "nowrap" }} className="dd-num">
              {moneyFmt(r.revenue)} · {r.units.toLocaleString()} units across history
            </span>
            {canEdit && (r.classified ? (
              <button
                type="button"
                style={stripBtn}
                onClick={() => onCostProduct(`Let's set costs for ${r.productName ?? r.label} (SKU ${r.skuCode})`)}
              >
                Cost this product
              </button>
            ) : (
              <Link to="/onboarding/chat" style={{ ...stripBtn, textDecoration: "none", display: "inline-block" }}>
                Categorize first
              </Link>
            ))}
          </div>
        ))}
      </div>
      {data.needsCosts.length > SHOW_ROWS && (
        <button type="button" onClick={() => setShowAll((v) => !v)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 11.5, padding: "6px 0 0", fontFamily: "inherit" }}>
          {showAll ? "Show fewer" : `Show all ${Math.min(data.needsCosts.length, 25)}${data.needsCostsCount > 25 ? ` of ${data.needsCostsCount}` : ""}`}
        </button>
      )}
    </div>}
    {data.historicalNeedsCostsCount > 0 && (
      <div style={{
        marginBottom: 26, padding: "14px 16px", borderRadius: 12,
        border: "1px solid color-mix(in srgb, var(--c2) 35%, var(--border))",
        background: "color-mix(in srgb, var(--c2) 5%, transparent)",
      }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 2 }}>
          Historical profit needs old costs: {data.historicalNeedsCostsCount} discontinued SKU{data.historicalNeedsCostsCount === 1 ? " has" : "s have"} no old costs
        </div>
        <div style={{ fontSize: 12.5, color: "var(--body)", marginBottom: 10, lineHeight: 1.5 }}>
          These SKUs stay out of current setup, but their past sales still count. Past profit is incomplete until the team adds their old product and shipping costs. The account stays in review until those gaps are resolved.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {data.historicalNeedsCosts.slice(0, SHOW_ROWS).map((row) => (
            <div key={row.skuId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{row.label}</span>
                <span style={{ color: "var(--faint)", fontSize: 11, fontFamily: "var(--font-mono)" }}>{row.skuCode}</span>
              </span>
              <span style={{ fontSize: 12, color: "var(--body)", whiteSpace: "nowrap" }} className="dd-num">
                {moneyFmt(row.revenue)} past sales
              </span>
            </div>
          ))}
        </div>
        {data.historicalNeedsCostsCount > SHOW_ROWS && (
          <div style={{ color: "var(--faint)", fontSize: 11.5, paddingTop: 7 }}>
            Showing the largest {SHOW_ROWS} of {data.historicalNeedsCostsCount} discontinued SKUs.
          </div>
        )}
      </div>
    )}
    </div>
  );
}

const stripBtn: React.CSSProperties = { fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 8, border: "1px solid var(--accent)", background: "var(--accent)", color: "var(--accent-contrast)", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" };

/* ----------------------------- parked costs ------------------------------ */

interface OverheadResp {
  overhead: { monthlyOverheadAmount: string; currency: string; notes: string | null } | null;
}

/** Costs mentioned during the setup chat that aren't counted anywhere yet.
 *  Two honest exits per row: fold a monthly $ into overhead, or dismiss. */
export function ParkedCostsCard({ canEdit }: { canEdit: boolean }): React.ReactElement | null {
  const api = useApiClient();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["parked-costs"],
    queryFn: () => api.get<{ parked: string[] }>("/business-costs/parked"),
  });

  const refresh = (): void => {
    relockFinancialQueries(qc);
    void qc.invalidateQueries({ queryKey: ["parked-costs"] });
    void qc.invalidateQueries({ queryKey: ["overhead"] });
    void qc.invalidateQueries({ queryKey: ["overhead-history"] });
  };

  const dismiss = useMutation({
    mutationFn: (note: string) => api.post("/business-costs/parked/dismiss", { note }),
    onSuccess: () => { refresh(); toast.success("Dismissed."); },
    onError: () => toast.error("Couldn't dismiss that — try again."),
  });

  const foldIntoOverhead = useMutation({
    mutationFn: async (input: { note: string; amount: number }) => {
      // Read the CURRENT overhead fresh (not from cache) so two quick folds
      // both land, then write the sum with a provenance note and clear the row.
      const cur = await api.get<OverheadResp>("/business-costs/overhead");
      const base = cur.overhead ? Number(cur.overhead.monthlyOverheadAmount) : 0;
      const currency = cur.overhead?.currency ?? "USD";
      const prior = cur.overhead?.notes ?? "";
      const added = `Includes ${moneyFmt(input.amount)}/mo folded in from parked cost: ${input.note}`;
      await api.post("/business-costs/overhead", {
        monthlyOverheadAmount: Math.round((base + input.amount) * 100) / 100,
        currency,
        source: "manual_estimate",
        notes: (prior ? `${prior} · ${added}` : added).slice(0, 500),
      });
      await api.post("/business-costs/parked/dismiss", { note: input.note });
    },
    onSuccess: (_d, input) => { refresh(); toast.success(`Added ${moneyFmt(input.amount)}/mo to your monthly overhead.`); },
    onError: () => toast.error("Couldn't add that to overhead — try again."),
  });

  const fold = (note: string): void => {
    const raw = window.prompt(`Roughly what does this cost per month?\n\n"${note}"\n\nEnter a monthly $ amount — it gets added to your monthly overhead.`);
    if (raw === null) return;
    const amount = Number(raw.replace(/[$,\s]/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Enter a monthly dollar amount, like 1500."); return; }
    foldIntoOverhead.mutate({ note, amount });
  };

  const parked = q.data?.parked ?? [];
  if (parked.length === 0) return null;
  return (
    <section style={{ marginBottom: 30 }}>
    <Card>
      <CardHead
        title="Parked costs"
        explain={[
          "<b>What it is:</b> Costs you mentioned during the setup chat that don't belong inside one product's margin — a team override, a seasonal surcharge, loyalty redemptions. They were saved here so they wouldn't get lost.",
          "<b>What to do:</b> They aren't counted in any number yet. Add one to your monthly overhead (a monthly $ estimate), or dismiss it if it's handled elsewhere.",
        ]}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {parked.map((note) => (
          <div key={note} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface-2)" }}>
            <span style={{ flex: 1, fontSize: 12.5, lineHeight: 1.45 }}>{note}</span>
            {canEdit && (
              <>
                <button type="button" style={ghost} disabled={foldIntoOverhead.isPending} onClick={() => fold(note)}>Add to monthly overhead</button>
                <button type="button" style={{ ...ghost, color: "var(--faint)" }} disabled={dismiss.isPending} onClick={() => dismiss.mutate(note)}>Dismiss</button>
              </>
            )}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginTop: 10 }}>
        Not counted in any margin or profit number until you place them.
      </div>
    </Card>
    </section>
  );
}

/* --------------------------- inventory financing -------------------------- */

interface Financing { financedPct: number; ratePct: number }

/** The line-of-credit terms (#19): share of stock bought on credit + APR.
 *  Saving re-derives the per-unit interest across every costed product on the
 *  server (from your real lead times and payment schedules) and re-runs the
 *  margin history, so the change lands everywhere. */
export function InventoryFinancingCard({ canEdit }: { canEdit: boolean }): React.ReactElement {
  const api = useApiClient();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["financing"],
    queryFn: () => api.get<{ financing: Financing | null }>("/business-costs/financing"),
  });

  // Drafts stay null until touched — the inputs show the saved values live.
  const [pctDraft, setPctDraft] = useState<string | null>(null);
  const [aprDraft, setAprDraft] = useState<string | null>(null);
  const saved = q.data?.financing ?? null;
  const savedPct = saved ? String(saved.financedPct) : "";
  const savedApr = saved ? String(saved.ratePct) : "";
  const pct = pctDraft ?? savedPct;
  const apr = aprDraft ?? savedApr;
  const dirty = (pctDraft !== null && pctDraft.trim() !== savedPct) || (aprDraft !== null && aprDraft.trim() !== savedApr);
  const numOk = (v: string): boolean => v.trim() === "" || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100);
  const valid = numOk(pct) && numOk(apr);

  const save = useMutation({
    mutationFn: () => api.post("/business-costs/financing", { financedPct: Number(pct) || 0, ratePct: Number(apr) || 0 }),
    onSuccess: () => {
      const cleared = !(Number(pct) > 0 && Number(apr) > 0);
      toast.success(cleared
        ? "Saved — no inventory financing. Any interest that was in your margins has been removed."
        : "Saved — the interest is charged in each product's margin, and your cash cycle counts only the cash you front.");
      setPctDraft(null); setAprDraft(null);
      relockFinancialQueries(qc);
      void qc.invalidateQueries({ queryKey: ["financing"] });
      // Interest is baked into per-unit CM — every margin surface moves.
      void invalidateCatalogQueries(qc);
    },
    onError: () => toast.error("Couldn't save — check the numbers (both are percentages, 0–100)."),
  });

  const active = saved !== null;
  return (
    <Card>
      <CardHead
        title="Inventory financing"
        explain={[
          "<b>What it is:</b> If you buy stock on a line of credit instead of paying cash, tell us what share of your inventory goes on it and the interest rate (APR).",
          "<b>How it's used:</b> Two honest things happen. The interest is charged against each unit's margin, over the days the money is actually out — if you've set up a supplier payment schedule, that schedule decides it (the days between paying your supplier and the sale coming back); otherwise it's your lead time minus supplier terms. And your cash cycle counts only the cash YOU front — the lender covers the rest, so borrowing shows up as faster cash, slightly less profit.",
          "<b>Clearing it:</b> Set either number to 0 if you fund all your stock yourself — the interest comes back out of your margins automatically.",
        ]}
      />
      {active && (
        <div style={{ fontSize: 12.5, color: "var(--body)", marginBottom: 12, lineHeight: 1.55 }}>
          <b>{saved.financedPct}%</b> of your stock is bought on a credit line at <b>{saved.ratePct}% APR</b> —
          the interest is charged against each unit's margin, and only the cash you front counts in your cash cycle.
        </div>
      )}
      {!active && (
        <div style={{ fontSize: 12.5, color: "var(--faint)", marginBottom: 12 }}>
          No inventory financing saved — your cash cycle assumes you front all of it.
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--body)" }}>
          Share of stock on credit
          <span style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <input value={pct} onChange={(e) => setPctDraft(e.target.value)} placeholder="0" inputMode="numeric" disabled={!canEdit} style={{ ...cellBox, width: 64, textAlign: "right", paddingRight: 22 }} />
            <span style={{ position: "absolute", right: 9, fontSize: 12, color: "var(--faint)" }}>%</span>
          </span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--body)" }}>
          Interest rate (APR)
          <span style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <input value={apr} onChange={(e) => setAprDraft(e.target.value)} placeholder="0" inputMode="decimal" disabled={!canEdit} style={{ ...cellBox, width: 64, textAlign: "right", paddingRight: 22 }} />
            <span style={{ position: "absolute", right: 9, fontSize: 12, color: "var(--faint)" }}>%</span>
          </span>
        </label>
        {canEdit && dirty && (
          <button type="button" style={{ ...primaryBtn, opacity: valid && !save.isPending ? 1 : 0.5 }} disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      {!canEdit && <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginTop: 8 }}>Read-only access — an owner or admin sets these.</div>}
    </Card>
  );
}

/* ----------------------------- processing fees ---------------------------- */

interface ProcessorRate { name: string; pct: number; fixed: number; sharePct: number; aliases?: string[] }
interface PaymentModel { pct: number; fixed: number; processors?: ProcessorRate[]; gatewayMonthly?: number }

/** Each payment processor's true rates, as captured in the costs chat. Shown
 *  read-only here — the chat knows how to re-blend the rates across your sales
 *  mix, so edits go through it ("Stripe is 2.9% + 30¢ now"). */
export function ProcessingFeesCard(): React.ReactElement {
  const api = useApiClient();
  const q = useQuery({
    queryKey: ["processing-fees"],
    queryFn: () => api.get<{ payment: PaymentModel | null }>("/business-costs/processing"),
  });
  const p = q.data?.payment ?? null;
  const rate = (pct: number, fixed: number): string =>
    `${Math.round(pct * 10000) / 100}%${fixed > 0 ? ` + ${fixed < 1 ? `${Math.round(fixed * 100)}¢` : moneyFmt(fixed)}` : ""}`;
  return (
    <Card>
      <CardHead
        title="Processing fees"
        explain={[
          "<b>What it is:</b> What each payment processor takes on a sale — a percent plus a per-charge fee — and roughly what share of your sales runs through it.",
          "<b>How it's used:</b> Each sale's margin is charged its own processor's rate when we know which processor ran it; otherwise the blended rate (weighted by share) stands in.",
          "<b>To change these:</b> tell the costs chat above — for example \"Stripe is 2.9% + 30¢ now\" — and it updates every product's margin for you.",
        ]}
      />
      {!p && (
        <div style={{ fontSize: 12.5, color: "var(--faint)" }}>
          No processing rates saved yet — the costs chat above asks about them during setup.
        </div>
      )}
      {p && (
        <>
          {p.processors && p.processors.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr style={{ color: "var(--text-faint)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    <th style={{ textAlign: "left", padding: "6px 8px 6px 0" }}>Processor</th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Takes per sale</th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Share of sales</th>
                  </tr>
                </thead>
                <tbody>
                  {p.processors.map((proc) => (
                    <tr key={proc.name} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={{ padding: "8px 8px 8px 0" }}>
                        <span style={{ fontWeight: 600 }}>{proc.name}</span>
                        {proc.aliases && proc.aliases.length > 0 && (
                          <div style={{ fontSize: 10.5, color: "var(--text-faint)", marginTop: 2 }}>also covers: {proc.aliases.join(", ")}</div>
                        )}
                      </td>
                      <td style={{ padding: "8px 8px" }} className="dd-num">{rate(proc.pct, proc.fixed)}</td>
                      <td style={{ padding: "8px 8px" }} className="dd-num">{proc.sharePct > 0 ? `${Math.round(proc.sharePct)}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: "var(--body)" }}>
              All sales: <b className="dd-num">{rate(p.pct, p.fixed)}</b> per sale.
            </div>
          )}
          {p.processors && p.processors.length > 1 && (
            <div style={{ fontSize: 12, color: "var(--body)", marginTop: 10 }}>
              Blended across your sales mix: <b className="dd-num">{rate(p.pct, p.fixed)}</b> per sale — used when we can't tell which processor ran a charge.
            </div>
          )}
          {p.gatewayMonthly !== null && p.gatewayMonthly !== undefined && p.gatewayMonthly > 0 && (
            <div style={{ fontSize: 12, color: "var(--faint)", marginTop: 6 }}>
              Plus {moneyFmt(p.gatewayMonthly)}/mo in flat gateway fees — folded into your monthly overhead (disclosed in its notes), never charged per sale.
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* --------------------------------- styles -------------------------------- */

const cellBox: React.CSSProperties = { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "6px 9px", fontSize: 12.5, color: "var(--text)", fontFamily: "inherit" };
const primaryBtn: React.CSSProperties = { background: "var(--accent)", color: "var(--accent-contrast)", border: "none", borderRadius: "var(--r-sm)", padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" };
const ghost: React.CSSProperties = { background: "transparent", color: "var(--body)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" };
